/**
 * Resilient Google Gemini AI Service for Lashary FB Market Suite
 * Supports current generation Google AI Studio models (Gemini 3.6 Flash, 3.5 Flash, 3.7 Flash)
 * with automatic fallback if a specific model endpoint is unavailable.
 */

const PREFERRED_MODELS = [
  'gemini-3.6-flash',
  'gemini-3.5-flash',
  'gemini-3.7-flash',
  'gemini-2.5-flash',
  'gemini-flash-latest',
];

export interface GeminiCallOptions {
  isJson?: boolean;
  temperature?: number;
}

export interface GeminiCallResult {
  success: boolean;
  text?: string;
  model?: string;
  error?: string;
}

/**
 * Validates a Gemini API Key against Google AI Studio with quick echo test
 */
export async function testGeminiApiKey(apiKey: string): Promise<GeminiCallResult> {
  const trimmed = apiKey.trim();
  if (!trimmed) {
    return { success: false, error: 'API key cannot be empty.' };
  }

  let lastError = 'No compatible Gemini model endpoint reached.';

  for (const model of PREFERRED_MODELS) {
    try {
      const url = `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${trimmed}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{ parts: [{ text: 'Respond with the single word: VERIFIED' }] }],
        }),
      });

      if (res.ok) {
        const data = await res.json();
        const output = data?.candidates?.[0]?.content?.parts?.[0]?.text?.trim();
        return {
          success: true,
          model,
          text: output || 'VERIFIED',
        };
      }

      const errData = await res.json().catch(() => null);
      if (errData?.error?.message) {
        lastError = `[${model}] ${errData.error.message}`;
      } else {
        lastError = `HTTP ${res.status} from model ${model}`;
      }
    } catch (err: unknown) {
      lastError = err instanceof Error ? err.message : 'Network error';
    }
  }

  return { success: false, error: lastError };
}

/**
 * Generates content using Google Gemini with automatic model failover
 */
export async function generateWithGemini(
  apiKey: string,
  prompt: string,
  options: GeminiCallOptions = {}
): Promise<GeminiCallResult> {
  const trimmed = apiKey.trim();
  if (!trimmed) {
    return { success: false, error: 'Google AI Studio API key is missing.' };
  }

  let lastError = 'Generation failed across all model endpoints.';

  for (const model of PREFERRED_MODELS) {
    try {
      const url = `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${trimmed}`;
      const requestBody: Record<string, unknown> = {
        contents: [{ parts: [{ text: prompt }] }],
      };

      const generationConfig: Record<string, unknown> = {
        temperature: options.temperature ?? 0.7,
      };

      if (options.isJson) {
        generationConfig.responseMimeType = 'application/json';
      }

      requestBody.generationConfig = generationConfig;

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      if (res.ok) {
        const data = await res.json();
        const text = data?.candidates?.[0]?.content?.parts?.[0]?.text;
        if (text) {
          return { success: true, text, model };
        }
      }

      const errData = await res.json().catch(() => null);
      if (errData?.error?.message) {
        lastError = `[${model}] ${errData.error.message}`;
      }
    } catch (err: unknown) {
      lastError = err instanceof Error ? err.message : 'Network failure';
    }
  }

  return { success: false, error: lastError };
}
